import angular from 'angular'
import merge from 'lodash/merge'
import each from 'lodash/each'
import find from 'lodash/find'

import { VdFeatureManager } from '@vend/vend.js'
import { $http, $log, $q } from '../../ng-compat/imports'
import { EntityStore } from '../../factories/database/entity-store'
import { getFeatureFromCache } from '../../util/feature-cache'

export const DEFAULT_PAGE_SIZE = 10000

/**
 * Provides a sync manager implementation for versioned entities.
 *
 * @module versionedEntitySyncManager
 */
angular
  .module('webregister.managers')
  .factory('VersionedEntitySyncManager', VersionedEntitySyncManagerFactory)
function VersionedEntitySyncManagerFactory(
  versionsStore: $TSFixMe,
  AbstractSyncManager: $TSFixMe,
  vdFeatureManager: VdFeatureManager
) {
  /**
   * Manages the syncing of versioned entities.
   *
   * @class VersionedEntitySyncManager
   * @constructor
   * @extends AbstractSyncManager
   *
   * @param {Object} config
   *
   *        @param {Array} config.managedEntityStores
   *               Collection of stores which should be managed
   */
  class VersionedEntitySyncManager extends AbstractSyncManager {
    /**
     * Prepare the entity with the given store name for resync. Clears the related entity store and resets the
     * related EntityVersion's `lastSyncedVersion` and `initialSyncHasOccurred` properties.
     *
     * @method prepareEntityForResync
     *
     * @param entityStoreName {String}
     *        The entity store name of the entity to prepare for resync
     *
     * @return {Promise} A promise resolved once the entity is ready for resync
     */
    public prepareEntityForResync(entityStoreName: string) {
      return this._getEntityStore(entityStoreName)
        .clear()
        .then(function () {
          return versionsStore.updateEntityVersion({
            entityStoreName,
            lastSyncedVersion: 0,
            initialSyncHasOccurred: false,
          })
        })
    }

    /**
     * Initialise a given entity store in preparation for syncing related entities. Ensures the versionsStore
     * is tracking the related entity.
     *
     * @protected
     * @method _initEntityStore
     *
     * @param entityStore {EntityStore}
     *        The entity store to initialise in preparation for syncing related entities
     *
     * @return {Promise} A promise resolved when the given entity store has been initialised for sync
     */
    protected _initEntityStore(entityStore: EntityStore<$TSFixMe>) {
      return versionsStore.trackVersion(
        entityStore.entityName,
        entityStore.entityStoreName
      )
    }

    /**
     * Get the list EntitySyncConfigs for the entities to sync. Augments the EntitySyncConfigs with the related
     * EntityVersions updated with the latest server versions.
     *
     * @protected
     * @method _getEntitiesToSync
     *
     * @return {Promise<Array<EntitySyncConfig>>} Promise resolved with the array of EntitySyncConfigs for the
     *         entities to sync
     */
    protected _getEntitiesToSync() {
      return $q
        .all({
          entitySyncConfigs: super._getEntitiesToSync(),
          updatedEntityVersions: this._requestUpdatedEntityVersions(),
        })
        .then(this._getEntityVersionsForSync.bind(this))
    }

    /**
     * Augment the given EntitySyncConfigs with the related EntityVersion if available.
     *
     * @private
     * @method _getEntityVersionsForSync
     *
     * @param data {Object}
     *
     *        @param data.entitySyncConfigs {Array<EntitySyncConfig>}
     *               Array of EntitySyncConfigs for the entities to sync
     *
     *        @param data.updatedEntityVersions {Array<EntityVersion>}
     *               Array of EntityVersions for the related entities, updated with latest server versions
     *
     * @return {Promise<Array<EntitySyncConfig>>} Promise resolved with the array of EntitySyncConfigs for the
     *         entities to sync
     */
    private _getEntityVersionsForSync(data: {
      entitySyncConfigs: $TSFixMe[]
      updatedEntityVersions: $TSFixMe[]
    }) {
      const entitySyncConfigs = data.entitySyncConfigs
      const updatedEntityVersions = data.updatedEntityVersions

      each(entitySyncConfigs, function (entitySyncConfig) {
        const entityStoreName = entitySyncConfig.entityStoreName
        const entityVersion = find(updatedEntityVersions, {
          entityStoreName,
        })

        if (entityVersion) {
          entitySyncConfig.data.entityVersion = entityVersion
        }
      })

      return entitySyncConfigs
    }

    /**
     * Requests the latest version data from the /api/2.0/versions end point, updates the versionsStore and
     * determines which entities need syncing.
     *
     * @private
     * @method _requestUpdatedEntityVersions
     *
     * @return {Promise<Array<EntityVersion>>} A promise resolved with an Array of entities that need to be
     *         synced (i.e. have max version greater than lastSynced version)
     */
    private _requestUpdatedEntityVersions() {
      return $http.get<$TSFixMe>('/api/2.0/versions').then(function (response) {
        return versionsStore.updateVersions(response.data.data)
      })
    }

    /**
     * Performs a single iteration for the sync of the entity specified by the given EntitySyncConfig. Requests
     * the next page of data, saves it to the entity store and updates the related EntityVersion.
     *
     * @protected
     * @method _doSyncEntity
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The EntitySyncConfig for the entity to sync
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the updated EntitySyncConfig when a single
     *         iteration of the sync is complete
     */
    protected _doSyncEntity(entitySyncConfig: $TSFixMe) {
      return this._requestEntityData(entitySyncConfig)
        .then(this._syncEntityData.bind(this))
        .then(this._updateEntityVersion.bind(this))
    }

    /**
     * Request the next batch of entities based on the given version information. Skips the given entity if the server
     * didn't return updated version information for this entity.
     *
     * Will use the `requestSyncData` hook method on the entity store if implemented, otherwise fetches via
     * `#_doRequestEntityData`.
     *
     * @private
     * @method _requestEntityData
     *
     * @param  entitySyncConfig {EntitySyncConfig}
     *         The sync config for the entity being synced
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the updated sync config (containing the next
     *         batch of entities from the appropriate API end point)
     */
    private _requestEntityData(entitySyncConfig: $TSFixMe) {
      const { entityVersion } = entitySyncConfig.data
      const entityStoreName = entitySyncConfig.entityStoreName
      const entityStore = this._getEntityStore(entityStoreName)

      if (!entityVersion) {
        // Server didn't return a version for this entity so nothing to sync
        return versionsStore
          .getEntityVersion(entityStoreName)
          .then(function (entityVersion: $TSFixMe) {
            entitySyncConfig.data = {
              entityVersion,
              entityData: {
                data: [],
                version: {
                  min: null,
                  max: null,
                },
              },
            }
            return entitySyncConfig
          })
      }

      let requestDataDeferred
      if (angular.isFunction(entityStore.requestSyncData)) {
        // Entity store implements custom data sync hook, use that to fetch next batch of data
        requestDataDeferred = entityStore.requestSyncData(entitySyncConfig)
      } else {
        requestDataDeferred = this._doRequestEntityData(entitySyncConfig)
      }
      return requestDataDeferred.then(function (data: $TSFixMe) {
        $log.debug(
          '[VersionedEntitySyncManager] DATA received for "' +
            entityStoreName +
            '":',
          data
        )

        entitySyncConfig.data.entityData = data
        return entitySyncConfig
      })
    }

    /**
     * Request the next batch of entities based on the given version information. Requests data using the standard
     * /api/2.0/<entity>/?deleted=1&before=<x>&after=<y> API.
     *
     * @private
     * @method _requestEntityData
     *
     * @param  entitySyncConfig {EntitySyncConfig}
     *         The sync config for the entity being synced
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the updated sync config (containing the next
     *         batch of entities from the appropriate API end point)
     */
    private _doRequestEntityData(entitySyncConfig: $TSFixMe) {
      const { entityVersion } = entitySyncConfig.data
      const entityStore = this._getEntityStore(entitySyncConfig.entityStoreName)
      const entityUrl = entityStore.getUrl(entitySyncConfig, vdFeatureManager)

      const pageSize = getFeatureFromCache('webregister_sync_batch_size_5k')
        ? 5000
        : DEFAULT_PAGE_SIZE

      const params: $TSFixMe = {
        after: entityVersion.lastSyncedVersion,
        page_size: pageSize,
      }

      if (entityStore.filterByOutlet) {
        params.outlet_id = entitySyncConfig.outletId
      }

      if (entityVersion.initialSyncHasOccurred || entityStore.syncDeleted) {
        params.deleted = 1
      } else {
        params.before = entityVersion.maxVersion + 1
      }

      if (entityStore.getAdditionalSyncParams) {
        const additionalParams = entityStore.getAdditionalSyncParams(
          entityVersion
        )
        if (additionalParams) {
          merge(params, additionalParams)
        }
      }

      return $http.get(entityUrl, { params }).then(response => response.data)
    }

    /**
     * Syncs a page of entity data with the appropriate IndexedDB store for the given entity.
     *
     * @private
     * @method _syncEntityData
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The sync config for the entity being synced
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the same EntitySyncConfig object passed in
     *         once the given entity data has been synced with the IndexedDB store
     */
    private _syncEntityData(entitySyncConfig: $TSFixMe) {
      const entitySyncData = entitySyncConfig.data
      const entityData = entitySyncData.entityData
      const entities = entityData.data

      if (entities.length && !entitySyncConfig.interrupt) {
        const entityVersion = entitySyncData.entityVersion
        const entityStore = this._getEntityStore(entityVersion.entityStoreName)

        return entityStore
          .syncData(entities, {
            payloadVersion: entityData.version,
            lastSyncedVersion: entityVersion.lastSyncedVersion,
          })
          .then(() => entitySyncConfig)
      } else {
        return entitySyncConfig
      }
    }

    /**
     * Update the EntityVersion for the current entity sync and determine whether the sync of this entity is
     * complete or not.
     *
     * @private
     * @method _updateEntityVersion
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The sync config for the entity being synced
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the EntitySyncConfig object (updated with
     *         the `syncComplete` flag set appropriately) after the EntityVersion has been updated and saved to
     *         the versionsStore
     */
    private _updateEntityVersion(entitySyncConfig: $TSFixMe) {
      const entitySyncData = entitySyncConfig.data
      const entityData = entitySyncData.entityData
      const dataReceived = entityData.data.length
      const entityVersion = entitySyncData.entityVersion
      let syncComplete = false
      let lastSyncedVersion

      if (entitySyncConfig.interrupt) {
        return entitySyncConfig
      }

      if (dataReceived) {
        lastSyncedVersion = entityData.version.max

        if (lastSyncedVersion >= entityVersion.maxVersion) {
          entityVersion.initialSyncHasOccurred = true
        }
      } else {
        lastSyncedVersion = Math.max(
          entityVersion.lastSyncedVersion,
          entityVersion.maxVersion
        )

        if (!entityVersion.initialSyncHasOccurred) {
          entityVersion.initialSyncHasOccurred = true
        } else {
          syncComplete = true
        }
      }

      entityVersion.lastSyncedVersion = lastSyncedVersion
      entityVersion.maxVersion = Math.max(
        entityVersion.maxVersion,
        lastSyncedVersion
      )

      return versionsStore
        .updateEntityVersion(entityVersion)
        .then(function (updatedEntityVersion: $TSFixMe) {
          entitySyncConfig.data.entityVersion = updatedEntityVersion
          entitySyncConfig.syncComplete = syncComplete

          return entitySyncConfig
        })
    }

    /**
     * Get the result (the latest EntityVersion) of a completed sync of the given entity.
     *
     * @protected
     * @method _getSyncResult
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The config for the sync of this entity
     *
     * @return {Any} The result of the sync of this entity
     */
    protected _getSyncResult(entitySyncConfig: $TSFixMe) {
      return entitySyncConfig.data.entityVersion
    }
  }

  return VersionedEntitySyncManager
}
